import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/layout';
import { voyageService, carService, printService } from '../services';
import { Voyage, Car, Shipper, Port, AdditionalFeeValue } from '../types';
import { SearchableMultiSelect, SearchableSelect, ShipperSelect } from '../components';
import Invoice from './Invoice';

interface RelatedCarItem {
  chassisNo: string;
  make: string;
  model: string;
  relationshipType: string;
  additionalFees: Record<string, AdditionalFeeValue>;
}

interface InvoiceCarItem {
  position: number;
  chassisNo: string;
  make: string;
  model: string;
  port: string;
  length: number | null;
  carType: string;
  freight: number;
  collect: number;
  additionalFees: Record<string, AdditionalFeeValue>;
  relatedCars?: RelatedCarItem[];
}

const InvoicePage: React.FC = () => {
  const [voyages, setVoyages] = useState<Voyage[]>([]);
  const [selectedVoyage, setSelectedVoyage] = useState<Voyage | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [selectedShipperId, setSelectedShipperId] = useState<string>('');
  const [selectedPortIds, setSelectedPortIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'port' | 'make/model' | 'chassis no.'>('port');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [includeRelated, setIncludeRelated] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [invoiceNumberError, setInvoiceNumberError] = useState<string>('');

  useEffect(() => {
    const loadVoyages = async () => {
      try {
        const data = await voyageService.getAll({ limit: 1000, is_archived: false });
        setVoyages(data as Voyage[]);
      } catch (error) {
        console.error('Error loading voyages:', error);
      }
    };
    loadVoyages();
  }, []);

  const sortedVoyages = useMemo(() => {
    return [...voyages].sort((a, b) => a.voyage_code.localeCompare(b.voyage_code));
  }, [voyages]);

  useEffect(() => {
    if (!selectedVoyage) {
      setCars([]);
      setSelectedShipperId('');
      return;
    }

    const loadCars = async () => {
      setLoading(true);
      try {
        const response = await carService.getByVoyage(selectedVoyage.id, { limit: 10000 });
        setCars(response.data as Car[]);
      } catch (error) {
        console.error('Error loading cars:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCars();
  }, [selectedVoyage]);

  const availableShippers = useMemo<Shipper[]>(() => {
    const shipperMap = new Map<string, Shipper>();

    cars.forEach(car => {
      const shipperId = car.shipper?.id || car.shipper_id;
      if (!shipperId) return;

      const code = car.shipper?.code || '';
      const name = car.shipper?.name || '';
      const shortname = car.shipper?.shortname || '';
      const address = car.shipper?.address || '';

      shipperMap.set(shipperId, {
        id: shipperId,
        code,
        name,
        shortname,
        address,
      });
    });

    return Array.from(shipperMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [cars]);

  // Get available ports for selected shipper
  const availablePorts = useMemo<Port[]>(() => {
    if (!selectedShipperId) return [];
    
    const portMap = new Map<string, Port>();
    cars.forEach(car => {
      if (car.shipper?.id !== selectedShipperId || !car.loaded) return;
      if (!car.port) return;
      
      portMap.set(car.port.id, car.port);
    });
    
    return Array.from(portMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [cars, selectedShipperId]);

  useEffect(() => {
    if (!selectedShipperId && availableShippers.length > 0) {
      setSelectedShipperId(availableShippers[0].id);
    } else if (selectedShipperId && !availableShippers.find(s => s.id === selectedShipperId)) {
      setSelectedShipperId('');
    }
  }, [availableShippers, selectedShipperId]);

  // Reset port selection when shipper changes
  useEffect(() => {
    setSelectedPortIds([]);
  }, [selectedShipperId]);

  const selectedShipper = useMemo(() => 
    availableShippers.find(s => s.id === selectedShipperId), 
    [availableShippers, selectedShipperId]
  );

  const filteredCars = useMemo(() => {
    return cars.filter(car => {
      if (!car.loaded || car.shipper?.id !== selectedShipperId) return false;
      
      // Apply port filter (empty array means all ports)
      if (selectedPortIds.length > 0) {
        return car.port && selectedPortIds.includes(car.port.id);
      }
      
      return true;
    });
  }, [cars, selectedShipperId, selectedPortIds]);

  const invoiceCars = useMemo<InvoiceCarItem[]>(() => {
    const mappedCars = filteredCars.map(car => {
      const freight = Number(car.freight) || 0;
      const collect = Number(car.collect) || 0;
      const carType = car.category ?? car.model?.type ?? '';
      const length = car.length ?? car.model?.length ?? null;

      // Get additional fees from the JSONB field
      const additionalFees: Record<string, AdditionalFeeValue> = {};
      if (car.additional_fees && typeof car.additional_fees === 'object') {
        Object.entries(car.additional_fees).forEach(([key, value]) => {
          // Handle both old format (number) and new format ({ amount, type })
          if (typeof value === 'number' && value !== 0) {
            // Old format: default to freight type
            additionalFees[key] = { amount: value, type: 'F' };
          } else if (typeof value === 'object' && value !== null && 'amount' in value) {
            // New format with type
            const feeValue = value as AdditionalFeeValue;
            if (feeValue.amount !== 0) {
              additionalFees[key] = { amount: feeValue.amount, type: feeValue.type || 'F' };
            }
          }
        });
      }

      // Build related cars array (sorted: Attached first, then STC)
      const relatedCars = [...(car.related_cars || [])]
        .sort((a, b) => {
          if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
          if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
          return 0;
        })
        .map(rc => {
        // Get additional fees from related car
        const rcAdditionalFees: Record<string, AdditionalFeeValue> = {};
        if (rc.additional_fees && typeof rc.additional_fees === 'object') {
          Object.entries(rc.additional_fees).forEach(([key, value]) => {
            if (typeof value === 'number' && value !== 0) {
              rcAdditionalFees[key] = { amount: value, type: 'F' };
            } else if (typeof value === 'object' && value !== null && 'amount' in value) {
              const feeValue = value as AdditionalFeeValue;
              if (feeValue.amount !== 0) {
                rcAdditionalFees[key] = { amount: feeValue.amount, type: feeValue.type || 'F' };
              }
            }
          });
        }

        const rcDbMake = rc.make?.name || '';
        const rcDbModel = rc.model?.name || '';
        // Use database make/model if available, otherwise use manual_make_model
        const rcMake = rcDbMake || rc.manual_make_model || '';
        const rcModel = rcDbMake ? rcDbModel : '';
        return {
          chassisNo: rc.chassis_number || '',
          make: rcMake,
          model: rcModel,
          manualMakeModel: rc.manual_make_model || undefined,
          relationshipType: rc.relationship_type || 'STC',
          additionalFees: rcAdditionalFees,
        };
      });

      const dbMake = car.make?.name || '';
      const dbModel = car.model?.name || '';
      // Use database make/model if available, otherwise use manual_make_model
      const make = dbMake || car.manual_make_model || '';
      const model = dbMake ? dbModel : ''; // Only use model if we have database make

      return {
        position: 0, // Will be set after sorting
        chassisNo: car.chassis_number || '',
        make,
        model,
        manualMakeModel: car.manual_make_model || undefined,
        port: car.port?.port || '',
        length,
        carType,
        freight,
        collect,
        additionalFees,
        relatedCars: relatedCars.length > 0 ? relatedCars : undefined,
      };
    });

    // Sort based on selected option
    const sortedCars = [...mappedCars].sort((a, b) => {
      switch (sortBy) {
        case 'port':
          return (a.port || '').localeCompare(b.port || '');
        case 'make/model':
          const makeCompare = (a.make || '').localeCompare(b.make || '');
          if (makeCompare !== 0) return makeCompare;
          return (a.model || '').localeCompare(b.model || '');
        case 'chassis no.':
          return (a.chassisNo || '').localeCompare(b.chassisNo || '');
        default:
          return 0;
      }
    });

    // Update positions after sorting
    return sortedCars.map((car, idx) => ({
      ...car,
      position: idx + 1,
    }));
  }, [filteredCars, sortBy]);

  // Clear invoice number when selections change (invoice number is generated on save)
  useEffect(() => {
    setInvoiceNumber('');
    setInvoiceNumberError('');
  }, [selectedVoyage, selectedShipper]);

  // Get selected port names for display
  const selectedPortNames = useMemo(() => {
    if (selectedPortIds.length === 0) return 'All Ports';
    return availablePorts
      .filter(p => selectedPortIds.includes(p.id))
      .map(p => p.code)
      .join(', ') || 'All Ports';
  }, [selectedPortIds, availablePorts]);

  const invoicePayload = useMemo(() => {
    if (!selectedVoyage || !selectedShipper) return null;
    const formattedDate = new Date().toLocaleDateString('en-GB');

    return {
      invoiceNumber: invoiceNumber || '(Generated on Save)',
      date: formattedDate,
      voyageNo: selectedVoyage.voyage_code,
      vesselName: selectedVoyage.vessel_description,
      shipperCode: selectedShipper.code,
      shipperName: selectedShipper.name,
      shipperAddress: selectedShipper.address,
      portFilter: selectedPortNames,
      pol: selectedVoyage.port_name || '',
      cars: invoiceCars,
      includeRelated,
    };
  }, [selectedVoyage, selectedShipper, invoiceNumber, invoiceCars, selectedPortNames, includeRelated]);

  const generateInvoice = async () => {
    if (!selectedVoyage || !selectedShipper || invoiceCars.length === 0) return;

    setGenerating(true);
    setInvoiceNumberError('');
    try {
      // Calculate total amount for the invoice
      const totalCollect = invoiceCars.reduce((sum, c) => {
        const baseCollect = c.collect;
        const collectFees = Object.values(c.additionalFees || {})
          .filter(fee => fee.type === 'C' || fee.type === 'B')
          .reduce((feeSum, fee) => feeSum + fee.amount, 0);
        const relatedCollectFees = (c.relatedCars || []).reduce((rcSum, rc) => {
          return rcSum + Object.values(rc.additionalFees || {})
            .filter(fee => fee.type === 'C' || fee.type === 'B')
            .reduce((feeSum, fee) => feeSum + fee.amount, 0);
        }, 0);
        return sum + baseCollect + collectFees + relatedCollectFees;
      }, 0);
      const totalFreight = invoiceCars.reduce((sum, c) => {
        const baseFreight = c.freight;
        const freightFees = Object.values(c.additionalFees || {})
          .filter(fee => fee.type === 'F' || fee.type === 'B')
          .reduce((feeSum, fee) => feeSum + fee.amount, 0);
        const relatedFreightFees = (c.relatedCars || []).reduce((rcSum, rc) => {
          return rcSum + Object.values(rc.additionalFees || {})
            .filter(fee => fee.type === 'F' || fee.type === 'B')
            .reduce((feeSum, fee) => feeSum + fee.amount, 0);
        }, 0);
        return sum + baseFreight + freightFees + relatedFreightFees;
      }, 0);
      const totalAmount = totalCollect - totalFreight;

      // Generate invoice number only when saving
      const result = await printService.getOrCreateInvoiceNumber(
        selectedVoyage.id,
        selectedShipper.id,
        selectedShipper.code,
        selectedPortNames,
        totalAmount
      );
      const newInvoiceNumber = result.invoiceNumber;
      setInvoiceNumber(newInvoiceNumber);

      // Build payload with the new invoice number
      const formattedDate = new Date().toLocaleDateString('en-GB');
      const payload = {
        invoiceNumber: newInvoiceNumber,
        date: formattedDate,
        voyageNo: selectedVoyage.voyage_code,
        vesselName: selectedVoyage.vessel_description,
        shipperCode: selectedShipper.code,
        shipperName: selectedShipper.name,
        shipperAddress: selectedShipper.address,
        portFilter: selectedPortNames,
        pol: selectedVoyage.port_name || '',
        cars: invoiceCars,
        includeRelated,
      };
      const filename = `Invoice_${newInvoiceNumber}_${selectedShipper.code}.pdf`;

      await printService.generateInvoice(payload, filename);
      console.log('Invoice generated successfully');
    } catch (error) {
      console.error('Error generating invoice:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setInvoiceNumberError(errorMsg);
      alert(`Failed to generate invoice: ${errorMsg}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Invoice</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Select voyage and shipper to generate an invoice for loaded vehicles.
          </p>

          <div className="flex gap-4 items-center flex-wrap">
            <div className="w-52">
              <SearchableSelect
                options={sortedVoyages.map(voyage => ({
                  value: voyage.id,
                  label: `${voyage.voyage_code} - ${voyage.vessel_description}`
                }))}
                value={selectedVoyage?.id || ''}
                onChange={(value) => {
                  const voyage = sortedVoyages.find(v => v.id === value);
                  setSelectedVoyage(voyage || null);
                }}
                placeholder="-- Select Voyage --"
              />
            </div>

            <div className="w-64">
              <ShipperSelect
                shippers={availableShippers}
                value={selectedShipperId}
                onChange={setSelectedShipperId}
                placeholder="-- Select Shipper --"
                disabled={availableShippers.length === 0}
              />
            </div>

            {/* Port Selection */}
            {selectedShipperId && availablePorts.length > 0 && (
              <SearchableMultiSelect
                options={availablePorts.map(port => ({
                  value: port.id,
                  label: `${port.code} - ${port.port}`
                }))}
                value={selectedPortIds}
                onChange={setSelectedPortIds}
                placeholder="Select Ports"
                allLabel="All Ports"
                className="w-64"
              />
            )}

            {/* Sort By Selection */}
            {selectedShipperId && invoiceCars.length > 0 && (
              <select
                className="w-48 px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={sortBy}
                onChange={e => setSortBy(e.target.value as 'port' | 'make/model' | 'chassis no.')}
              >
                <option value="port">Sort by Port</option>
                <option value="make/model">Sort by Make/Model</option>
                <option value="chassis no.">Sort by Chassis No.</option>
              </select>
            )}

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeRelated}
                onChange={(e) => setIncludeRelated(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Include Related</span>
            </label>

            {invoiceNumberError && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <span className="text-sm text-red-600 dark:text-red-300">⚠️ {invoiceNumberError}</span>
              </div>
            )}

            <button
              onClick={generateInvoice}
              disabled={!selectedVoyage || !selectedShipper || invoiceCars.length === 0 || generating || loading}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
            >
              {generating ? 'Generating...' : 'Save as PDF'}
            </button>
          </div>

          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {!selectedVoyage && 'Choose a voyage to load cars.'}
            {selectedVoyage && loading && 'Loading cars...'}
            {selectedVoyage && !loading && !selectedShipper && 'Select a shipper to preview invoice.'}
            {selectedVoyage && !loading && selectedShipper && invoiceCars.length === 0 && 'No loaded cars found for this shipper.'}
            {selectedVoyage && !loading && selectedShipper && invoiceCars.length > 0 && `${invoiceCars.length} vehicle(s) ready for invoice.`}
          </div>
        </div>

        {loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">Loading cars...</p>
          </div>
        )}

        {selectedVoyage && selectedShipper && !loading && invoiceCars.length > 0 && invoicePayload && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Preview (Portrait A4)</span>
            </div>
            <div className="p-4 flex justify-center">
              <div className="w-full max-w-[210mm] bg-white shadow-lg border border-gray-300">
                <Invoice {...invoicePayload} />
              </div>
            </div>
          </div>
        )}

        {!selectedVoyage && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="text-gray-400 dark:text-gray-500">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Select a voyage and shipper to preview the invoice</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InvoicePage;

